/**
 *  Component: (common) Plugin Features
 */
import React, { useState, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Grid } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { InfoWorkZero } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { routes } from 'constants/routes';

import {
  getChannels,
  getSlackApplauseDetails,
  saveSlackApplauseMapping,
  updateSlackApplauseChannelMapping,
} from 'services/plugins';
import { showErrorSnackbar } from 'services/snackbar';

import { TextLabel } from 'uiToolkitV1/Typography';

import { StepCard } from '../commonComponents';
import { ApplauseContainer } from './linkTeamWithChannel';

const PluginFeatures = ({ thirdPartyType = '' }) => {
  const dispatch = useDispatch();
  const [dataLoading, setDataLoading] = useState(false);

  const [applauseFieldStatus, setapplauseFieldStatus] = useState([]);

  const [channels, setChannels] = useState([]);

  const getAllChannels = () => {
    setDataLoading(true);
    getChannels({ thirdPartyType })
      .then((res) => {
        if (res.length) {
          setChannels(res.map((channel) => ({ value: channel.id, label: channel.name })));
        } else {
          dispatch(showErrorSnackbar('No channels were found'));
          setDataLoading(false);
        }
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry, unable to fetch the channels!'));
        setDataLoading(false);
      });
  };

  const getSlackApplause = () => {
    setDataLoading(true);
    getSlackApplauseDetails()
      .then((res) => {
        setapplauseFieldStatus(
          res.map((channel) => ({
            value: channel.thirdPartyTeamId,
            label: channel.thirdPartyTeamName,
            uuid: channel.uuid,
          }))
        );
      })
      .catch(() => dispatch(showErrorSnackbar('Sorry, unable to fetch the mappings!')))
      .finally(() => setDataLoading(false));
  };

  useEffect(() => {
    setDataLoading(true);
    getSlackApplause();
    getAllChannels();
  }, []);

  const updateApplauseMapping = (value, label, mappingId) => {
    updateSlackApplauseChannelMapping({
      thirdPartyTeamId: value,
      thirdPartyTeamName: label,
      mappingId,
    })
      .then(() => {
        getSlackApplause();
      })
      .catch(() => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')));
  };

  const saveApplauseMapping = (value, label) => {
    saveSlackApplauseMapping({ thirdPartyTeamId: value, thirdPartyTeamName: label })
      .then(() => {
        getSlackApplause();
      })
      .catch(() => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')));
  };

  return dataLoading ? (
    <Grid container>
      <Grid item xs={5}>
        <Skeleton height="30px" />
        <Skeleton height="30px" />
      </Grid>
      <Grid item xs={2} />
      <Grid item xs={5}>
        <Skeleton height="30px" width="80%" />
        <Skeleton height="30px" width="80%" />
      </Grid>
    </Grid>
  ) : (
    <StepCard title="Link slack channel for public feedback">
      <div>
        {applauseFieldStatus.length ? (
          <ApplauseContainer channels={channels} updateFn={updateApplauseMapping} value={applauseFieldStatus} />
        ) : (
          <ApplauseContainer channels={channels} saveFn={saveApplauseMapping} />
        )}
      </div>
      <Flex alignItems="center">
        <Box ml="-6px">
          <InfoWorkZero fontSize="24px" color="#666" />
        </Box>

        <Flex>
          <TextLabel color="text.g2">Configure personalized Slack notifications from</TextLabel>
          <Link style={{ textDecoration: 'none' }} to={routes.settings.notifications}>
            <TextLabel cursor="pointer" color="button.blue.default" ml="4px">
              Notifications Settings
            </TextLabel>
          </Link>
        </Flex>
      </Flex>
    </StepCard>
  );
};

export { PluginFeatures };
